import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';

import UIImage from './ui/UIImage';
import UISelectValue from './ui/UISelectValue';

const initialState = {
  type: 'Postal',
  description: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  kicker: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  title: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  subtitle: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  url: '',
  image: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  isFullWidth: true,
  imageSize: '2880x1510',
  urlLocalized: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
};

const LOCALIZED_INPUT = 'LOCALIZED_INPUT';
const INITIALIZE_COMPONENT = 'INITIALIZE_COMPONENT';

export const setInitializedComponent = dispatch => component => dispatch({ type: INITIALIZE_COMPONENT, component });
export const setValue = dispatch => (name, value) => dispatch({ name, value });
const setValueLocalized = dispatch => (name, value, language) => dispatch({ type: LOCALIZED_INPUT, language, name, value });

export const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_COMPONENT: return action.component;
    case LOCALIZED_INPUT: return ({ ...state, [action.name]: { ...state[action.name], [action.language]: action.value } });
    default: return ({ ...state, [action.name]: action.value });
  }
};

const Postal = ({ activeLanguage, components, handlerSetComponents, id }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const componentIndex = components.findIndex(obj => obj.id === id);
  const { component } = components[componentIndex];

  useEffect(() => {
    if (component) setInitializedComponent(dispatch)(component);
  }, []);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setValue(dispatch)(name, value);
  };

  const handleInputLocalized = (e) => {
    const { name, value } = e.target;
    setValueLocalized(dispatch)(name, value, activeLanguage);
  };

  const handleImage = (imageUrl) => {
    setValueLocalized(dispatch)('image', imageUrl, activeLanguage);
  };

  useEffect(() => {
    const updatedComponent = {
      id,
      component: state,
    };

    handlerSetComponents(updatedComponent, componentIndex);
  }, [state]);

  return (
    <div className="text-black">
      <UIImage activeLanguage={activeLanguage} getImageUrl={handleImage} imageUrl={state.image} isFromPostal />
      <div className="flex-1 mr-4">
        <label htmlFor="title" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Title
          <input
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            name="title"
            onChange={handleInputLocalized}
            type="text"
            value={state.title[activeLanguage]}
          />
        </label>
        <label htmlFor="subtitle" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Subtitle
          <input
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            name="subtitle"
            onChange={handleInputLocalized}
            type="text"
            value={state.subtitle[activeLanguage]}
          />
        </label>
        <label htmlFor="kicker" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Kicker (text above the title)
          <input
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            name="kicker"
            onChange={handleInputLocalized}
            type="text"
            value={state.kicker[activeLanguage]}
          />
        </label>
        <label htmlFor="url" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Url
          <input
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            name="url"
            onChange={handleInput}
            type="text"
            value={state.url}
          />
        </label>
        <label htmlFor="urlLocalized" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Localized URL (In order to work Url should be empty)
          <input
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            name="urlLocalized"
            onChange={handleInputLocalized}
            type="text"
            value={state.urlLocalized?.[activeLanguage]}
          />
        </label>
        <UISelectValue
          label="Image Size"
          name="imageSize"
          value={state.imageSize}
          onChange={handleInput}
          options={[
            { value: '2880x1510', label: 'Normal' },
            { value: '2880x755', label: 'Landscape' },
          ]}
        />
      </div>
      <div className="flex-1 mr-4">
        <label htmlFor="description" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Description
          <textarea
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            name="description"
            onChange={handleInputLocalized}
            rows="5"
            type="text"
            value={state.description[activeLanguage]}
          />
        </label>
      </div>
    </div>
  );
};

Postal.propTypes = {
  activeLanguage: PropTypes.oneOf(['en', 'es', 'ca', 'pt']).isRequired,
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default Postal;
